import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { postRepairOderListApi } from 'api/repairOrder';
import DragAndResizeModal from 'components/Modal/DragAndResizeModal';
import { MAX_RO_COUNT_TO_REQUEST, RO_REQUEST_TIP } from 'constants/repairOrder';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import useOpenROListPopup from './Hooks/useOpenROListPopup';

const OpenROListPopup = (): JSX.Element => {
  const { handleCloseROListPopup, isPopupOpened } = useOpenROListPopup();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const selectedRoofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId) ?? '';

  const [roValues, setRoValues] = useState('');
  const [roParsingResult, setRoParsingResult] = useState<{
    valid: string[];
    invalid: string[];
  } | null>(null);
  const { isMobile } = useResponsive();

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setRoValues(event.target.value);
  };

  useEffect((): void => {
    if (isPopupOpened) {
      setRoValues('');
      setRoParsingResult(null);
    }
  }, [isPopupOpened]);

  const handleROListParsing = (): void => {
    const repairOrderListItems = roValues.split(',').map((token): string => token.trim());
    const validRepairOrderNumbers: string[] = [];
    const invalidRepairOrderNumbers: string[] = [];

    if (repairOrderListItems.length <= MAX_RO_COUNT_TO_REQUEST) {
      for (const token of repairOrderListItems) {
        if (token.match(/^\w+$/)) {
          validRepairOrderNumbers.push(token);
        } else {
          invalidRepairOrderNumbers.push(token);
        }
      }
      setRoParsingResult({
        valid: validRepairOrderNumbers,
        invalid: invalidRepairOrderNumbers,
      });
    } else {
      alert(`Please enter only up to ${MAX_RO_COUNT_TO_REQUEST} ROs`);
    }
  };

  const handleClearROParsing = (): void => {
    setRoParsingResult(null);
  };

  const handleSendListToWarrCloud = async (): Promise<void> => {
    if (!roParsingResult?.valid) return;
    try {
      await postRepairOderListApi(selectedRoofTopId, roParsingResult.valid);
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: "RO's successfully sent to WarrCloud",
          snackBarSeverity: 'success',
        })
      );
    } catch {
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: "Something went wrong sending RO's to WarrCloud",
          snackBarSeverity: 'error',
        })
      );
    }
    handleCloseROListPopup();
  };

  return (
    <DragAndResizeModal
      open={isPopupOpened}
      handleClose={handleCloseROListPopup}
      modalTitle="Send RO's to WarrCloud"
      infoTooltip={RO_REQUEST_TIP}
      size={ModalSizeE.SEND_RO}
      popupId={ScrollLockIdE.OpenROListPopup}
      isMobile={isMobile}
    >
      <Box px={3} pt={1} pb={3} height="100%" display="flex" flexDirection="column">
        {!roParsingResult ? (
          <Box display="flex" flexDirection="column" width="100%" height="100%">
            <Box width="100%" flexGrow={1}>
              <TextField
                multiline
                fullWidth
                minRows={6}
                value={roValues}
                placeholder="Enter RO numbers separated by commas..."
                onChange={handleChange}
                sx={{
                  height: '100%',
                  '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                }}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                disabled={roValues.length <= 0}
                onClick={handleROListParsing}
              >
                Send
              </Button>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" width="100%" height="100%">
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              overflow="auto"
              flexGrow={1}
              px={2}
              py={1}
              sx={{
                borderRadius: 2,
              }}
            >
              <Typography fontWeight="bold" mb={0.5}>
                Valid ROs:
              </Typography>
              {roParsingResult.valid.length > 0 ? (
                roParsingResult.valid.map(
                  (ro, index): JSX.Element => <li key={`valid-${index}`}>{ro}</li>
                )
              ) : (
                <Typography mb={0.5}>None</Typography>
              )}
              <br />
              <Typography fontWeight="bold" mb={0.5}>
                Invalid ROs:
              </Typography>
              {roParsingResult.invalid.length > 0 ? (
                roParsingResult.invalid.map(
                  (ro, index): JSX.Element => <li key={`invalid-${index}`}>{ro}</li>
                )
              ) : (
                <Typography mb={0.5}>None</Typography>
              )}
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
              <Button size="medium" variant="outlined" onClick={handleClearROParsing}>
                Back
              </Button>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                disabled={roParsingResult?.valid?.length <= 0}
                onClick={handleSendListToWarrCloud}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </DragAndResizeModal>
  );
};

export default OpenROListPopup;
